import React, { useEffect, useRef } from 'react';
import './Hero.css';
import profileImage from '../Assets/profile.jpeg';
import coverImage from '../Assets/cover.png';
import { motion } from '../utils/motion';

import { gsap } from 'gsap';

const heroBg = coverImage;

const Hero = () => {
    const heroRef = useRef(null);
    const profileRef = useRef(null);

    useEffect(() => {
        const hero = heroRef.current;
        if (!hero) return;

        const handleMouseMove = (e) => {
            const rect = hero.getBoundingClientRect();
            const x = ((e.clientX - rect.left) / rect.width - 0.5) * 25;
            const y = ((e.clientY - rect.top) / rect.height - 0.5) * 25;
            
            hero.style.setProperty('--mouse-x', `${x}px`);
            hero.style.setProperty('--mouse-y', `${y}px`);

            if (profileRef.current) {
                gsap.to(profileRef.current, {
                    rotationY: x * 0.5,
                    rotationX: -y * 0.5,
                    duration: 0.6,
                    ease: 'power2.out'
                });
            }
        };

        hero.addEventListener('mousemove', handleMouseMove);
        return () => hero.removeEventListener('mousemove', handleMouseMove);
    }, []);

    // Framer Motion Animation Variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.15,
                delayChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 35 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] }
        }
    };

    return (
        <section className="hero" id="home" ref={heroRef}>
            {/* Newly Generated Futuristic Background Image with Overlay */}
            <div 
                className="hero__bg-image" 
                style={{ backgroundImage: `url(${heroBg})` }}
            />
            <div className="hero__bg-overlay" />

            {/* Decorative Ambient Shapes */}
            <div className="hero__shapes">
                <div className="hero__shape hero__shape--1" />
                <div className="hero__shape hero__shape--2" />
                <div className="hero__shape hero__shape--3" />
                <div className="hero__shape hero__shape--ring" />
            </div>

            <motion.div 
                className="hero__content"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                {/* Status Badge */}
                <motion.div className="hero__badge" variants={itemVariants}>
                    <span className="hero__badge-dot" />
                    <span>Creating Digital Magic</span>
                </motion.div>

                {/* Profile Avatar */}
                <motion.div 
                    className="hero__profile" 
                    variants={itemVariants}
                    ref={profileRef}
                    whileHover={{ scale: 1.05, rotate: 2 }}
                    transition={{ type: "spring", stiffness: 300, damping: 15 }}
                >
                    <div className="hero__profile-glow" />
                    <div className="hero__profile-ring" />
                    <img src={profileImage} alt="Life Be Like Studio" className="hero__profile-img" />
                </motion.div>

                {/* Title */}
                <motion.h1 className="hero__title" variants={itemVariants}>
                    <span className="hero__title-line hero__title-line--1">
                        Life Be
                    </span>
                    <span className="hero__title-line hero__title-line--2">
                        <span className="hero__title-gradient">Like</span>
                        <span className="hero__title-sparkle">✦</span>
                    </span>
                </motion.h1>

                {/* Tagline */}
                <motion.p className="hero__tagline" variants={itemVariants}>
                    We craft <strong>viral content</strong>, AI-powered video magic,
                    and high-converting social media experiences that captivate millions.
                </motion.p>

                {/* Call to Actions */}
                <motion.div className="hero__actions" variants={itemVariants}>
                    <motion.a
                        href="https://www.facebook.com/share/19i2p1Ab8M/?mibextid=wwXIfr"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hero__btn hero__btn--primary"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.96 }}
                    >
                        <span>Visit Our Page</span>
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                            <path d="M3 15L15 3M15 3H6M15 3V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </motion.a>
                    <motion.a 
                        href="#services" 
                        className="hero__btn hero__btn--ghost"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.96 }}
                    >
                        <span>Explore Work</span>
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                            <path d="M9 3V15M9 15L4 10M9 15L14 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </motion.a>
                </motion.div>

                {/* Stats Counter */}
                <motion.div className="hero__stats" variants={itemVariants}>
                    <div className="hero__stat">
                        <span className="hero__stat-number">1K+</span>
                        <span className="hero__stat-label">Viral Content</span>
                    </div>
                    <div className="hero__stat-divider" />
                    <div className="hero__stat">
                        <span className="hero__stat-number">500K+</span>
                        <span className="hero__stat-label">Total Reach</span>
                    </div>
                    <div className="hero__stat-divider" />
                    <div className="hero__stat">
                        <span className="hero__stat-number">24/7</span>
                        <span className="hero__stat-label">AI Studio</span>
                    </div>
                </motion.div>
            </motion.div>

            {/* Scroll Indicator */}
            <motion.div 
                className="hero__scroll"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2, duration: 0.8 }}
            >
                <span className="hero__scroll-text">Scroll Down</span>
                <div className="hero__scroll-line" />
            </motion.div>
        </section>
    );
};

export default Hero;
